import { useRef, useState } from "react";
import { Link } from "wouter";
import { useAuthActions } from "@convex-dev/auth/react";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, Btn, PillTag, Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import DisciplineBadgesCard from "@/components/mnf/DisciplineBadgesCard";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  setCurrentUserOverride,
} from "@/lib/store";
import { stopAutoSave } from "@/lib/snapshotSync";

/**
 * Profile
 *
 * The sister's home for everything personal: her photo, lifetime stats,
 * spiritual discipline badges, and a tile grid linking to Edit Profile,
 * Billing, Purchases, Favorite meals, My Recipes. Below the tiles, a
 * Settings list (Notifications, Units) and a Support list (Getting started,
 * Suggest a feature, legal docs, Sign out). Modeled loosely on the
 * Trainerize pattern Kiley shared, but reskinned in forest/cream and
 * grace-leaning rather than performance-leaning.
 *
 * Wearables (Apple Health, Fitbit, etc.) are intentionally not shown here
 * because Apple App Review rejects "coming soon" sections. We will add a
 * Connect section once a real integration ships.
 */

/* --------------------------- Avatar upload helper -------------------------- */

/**
 * Reads the chosen file, draws it onto a 512x512 square canvas with a
 * center-crop, and returns a JPEG data URL. We resize to 512px to keep the
 * stored avatar small (under ~80KB) since we keep it in memory only.
 */
function readAndSquareCrop(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read photo."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not open photo."));
      img.onload = () => {
        const SIZE = 512;
        const canvas = document.createElement("canvas");
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas not available."));
          return;
        }
        // Center-crop the source: take the smaller dimension as the side.
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);
        try {
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        } catch (e) {
          reject(e instanceof Error ? e : new Error("Could not encode photo."));
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/* --------------------------------- Tile ----------------------------------- */

function AccountTile({
  href,
  icon,
  label,
  sublabel,
  testid,
}: {
  href: string;
  icon: string;
  label: string;
  sublabel?: string;
  testid: string;
}) {
  return (
    <Link href={href} className="no-underline" data-testid={testid}>
      <div
        className="flex flex-col items-center justify-center text-center hover-elevate"
        style={{
          background: "var(--mnf-surface)",
          border: "1px solid rgba(30,74,53,0.10)",
          borderRadius: 18,
          padding: "18px 10px 14px",
          height: 116,
          color: "var(--mnf-tx)",
        }}
      >
        <div
          className="inline-flex items-center justify-center mb-2"
          style={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            background: "rgba(30,74,53,0.08)",
            color: "var(--mnf-f)",
          }}
        >
          <Icon name={icon} size={22} />
        </div>
        <div
          className="mnf-body"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--mnf-fd)",
            lineHeight: 1.2,
          }}
        >
          {label}
        </div>
        {sublabel && (
          <div
            className="mnf-body mt-0.5"
            style={{ fontSize: 11, color: "var(--mnf-tg)", letterSpacing: "0.04em" }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </Link>
  );
}

/* ----------------------------- List row -------------------------------- */

function ListRow({
  href,
  icon,
  label,
  trailing,
  testid,
  onClick,
  danger,
}: {
  href?: string;
  icon: string;
  label: string;
  trailing?: React.ReactNode;
  testid: string;
  onClick?: () => void;
  danger?: boolean;
}) {
  const inner = (
    <div
      className="flex items-center gap-3 hover-elevate"
      style={{
        padding: "14px 16px",
        borderBottom: "1px solid rgba(30,74,53,0.08)",
        cursor: onClick || href ? "pointer" : "default",
      }}
    >
      <Icon
        name={icon}
        size={20}
        style={{ color: danger ? "var(--mnf-coral)" : "var(--mnf-f)" }}
      />
      <div
        className="mnf-body flex-1"
        style={{
          fontSize: 15,
          color: danger ? "var(--mnf-coral)" : "var(--mnf-tx)",
          fontWeight: danger ? 600 : 500,
        }}
      >
        {label}
      </div>
      {trailing}
      {(href || onClick) && !danger && (
        <Icon name="chevron_right" size={18} style={{ color: "var(--mnf-tg)" }} />
      )}
    </div>
  );
  if (href) {
    return (
      <Link href={href} className="no-underline block" data-testid={testid}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left"
      data-testid={testid}
    >
      {inner}
    </button>
  );
}

/* ============================== Page ====================================== */

export default function Profile() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account. When the signed-in identity is unknown, a
  // demo-account read here would show her streak/billing/handle as this
  // sister's own — the exact bug this page is fixing. Render nothing instead.
  const myId = me?.id ?? null;
  const stats = useStoreQuery(() => (myId ? queries.profileStats(myId) : null));
  const sub = useStoreQuery(() => (myId ? queries.subscriptionForUser(myId) : null));
  const disciplineStates = useStoreQuery(() =>
    myId ? queries.disciplinesForUser(myId) : [],
  );

  const setAvatar = useStoreMutation(mutations.setAvatar);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const { signOut } = useAuthActions();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  // Sign-out lives on this page and nowhere else. Returning null when the
  // profile data is thin therefore does not just hide a page — it removes the
  // only exit from the app, so a member signed into the wrong account has no
  // way back to the sign-in screen short of clearing site data by hand.
  // That happened to a real person. Render a minimal shell with the sign-out
  // control instead of nothing.
  if (!me || !stats) {
    return (
      <div style={{ padding: 24, display: "grid", gap: 16, justifyItems: "center" }}>
        <p style={{ textAlign: "center", opacity: 0.8 }}>
          {me
            ? "We couldn't load this profile yet."
            : "You're not signed in to a profile."}
        </p>
        <button
          type="button"
          disabled={signingOut}
          onClick={async () => {
            setSigningOut(true);
            setSignOutError(null);
            try {
              await signOut();
            } catch {
              setSignOutError("Sign out failed. Try again.");
              setSigningOut(false);
            }
          }}
        >
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
        {signOutError ? (
          <p style={{ color: "crimson", fontSize: 13 }}>{signOutError}</p>
        ) : null}
      </div>
    );
  }

  const initials = me.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handlePickPhoto = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError("Photo is too large. Please choose one under 10 MB.");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readAndSquareCrop(file);
      setAvatar(me.id, dataUrl);
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "Could not save your photo.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    if (!me.avatarDataUrl) return;
    if (window.confirm("Remove your profile photo?")) {
      setAvatar(me.id, null);
    }
  };

  const tierLabel = me.tier;
  const last4 = sub?.paymentMethodLast4 ?? null;

  // NOTE: there is deliberately no local "You are signed out." screen here.
  // A real signOut() flips useConvexAuth to unauthenticated, and GatedRoutes
  // in App.tsx swaps the whole tree for <SignIn />. Painting our own farewell
  // screen was how this page faked a sign-out that never happened.

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* Forest hero with avatar + camera badge */}
      <div style={{ position: "relative" }}>
        <ScreenHeader
          variant="forest"
          eyebrow="My profile"
          title="You, in bloom"
          trailing={
            <Link href="/settings" className="no-underline" data-testid="link-profile-settings">
              <div
                className="inline-flex items-center justify-center hover-elevate"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: "rgba(245,240,232,0.12)",
                  color: "#F5F0E8",
                }}
              >
                <Icon name="settings" size={20} />
              </div>
            </Link>
          }
        >
          <div className="flex flex-col items-center mt-2" style={{ gap: 12 }}>
            <div className="relative" style={{ width: 132, height: 132 }}>
              <Avatar
                initials={initials}
                size={132}
                src={me.avatarDataUrl ?? undefined}
                ringColor="rgba(245,240,232,0.45)"
              />
              <button
                type="button"
                onClick={handlePickPhoto}
                disabled={uploading}
                aria-label="Change profile photo"
                data-testid="button-change-avatar"
                className="hover-elevate active-elevate-2"
                style={{
                  position: "absolute",
                  right: -2,
                  bottom: 2,
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "var(--mnf-coral)",
                  color: "#FFF",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2.5px solid #163828",
                  cursor: uploading ? "wait" : "pointer",
                }}
              >
                <Icon name={uploading ? "hourglass_empty" : "photo_camera"} size={18} />
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
              data-testid="input-avatar-file"
            />
            <div style={{ textAlign: "center" }}>
              <div
                className="mnf-serif"
                style={{ fontSize: 22, color: "#F5F0E8", fontWeight: 500, lineHeight: 1.1 }}
                data-testid="text-profile-name"
              >
                {me.name}
              </div>
              <div
                className="mnf-body"
                style={{
                  fontSize: 13,
                  color: "rgba(245,240,232,0.78)",
                  marginTop: 4,
                  letterSpacing: "0.04em",
                }}
              >
                {tierLabel} sister
                {me.city ? ` · ${me.city}` : ""}
              </div>
              {me.avatarDataUrl && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  data-testid="button-remove-avatar"
                  className="mnf-body mt-2"
                  style={{
                    fontSize: 12,
                    color: "rgba(245,240,232,0.7)",
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Remove photo
                </button>
              )}
            </div>
            {uploadError && (
              <div
                className="mnf-body"
                role="alert"
                data-testid="text-avatar-error"
                style={{
                  fontSize: 12,
                  color: "#FFC9B0",
                  textAlign: "center",
                  maxWidth: 260,
                  marginTop: 4,
                }}
              >
                {uploadError}
              </div>
            )}
          </div>
        </ScreenHeader>
      </div>

      {/* Hero stats: streak + lifetime movements */}
      <div style={{ padding: "18px 16px 0", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <GlassCard padding={16} data-testid="card-stat-streak">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
                Grace streak
              </div>
              {stats.streak > 0 ? (
                <>
                  <div
                    className="mnf-serif mt-1"
                    style={{ fontSize: 32, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1 }}
                    data-testid="text-streak-value"
                  >
                    {stats.streak}
                  </div>
                  <div className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 4 }}>
                    {stats.streak === 1 ? "day showing up" : "days showing up"}
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="mnf-serif mt-1"
                    style={{ fontSize: 20, fontWeight: 500, color: "var(--mnf-tg)", lineHeight: 1.2 }}
                    data-testid="text-streak-empty"
                  >
                    No streak yet
                  </div>
                  <div className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 4 }}>
                    Show up today to start one
                  </div>
                </>
              )}
            </div>
            <Icon name="local_fire_department" size={28} style={{ color: "var(--mnf-coral)" }} />
          </div>
        </GlassCard>
        <GlassCard padding={16} data-testid="card-stat-movements">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
                Total movements
              </div>
              {stats.lifetimeMovements > 0 ? (
                <>
                  <div
                    className="mnf-serif mt-1"
                    style={{ fontSize: 32, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1 }}
                    data-testid="text-movements-value"
                  >
                    {stats.lifetimeMovements}
                  </div>
                  <div className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 4 }}>
                    logged in your garden
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="mnf-serif mt-1"
                    style={{ fontSize: 20, fontWeight: 500, color: "var(--mnf-tg)", lineHeight: 1.2 }}
                    data-testid="text-movements-empty"
                  >
                    None logged yet
                  </div>
                  <div className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 4 }}>
                    Your first set will show up here
                  </div>
                </>
              )}
            </div>
            <Icon name="fitness_center" size={28} style={{ color: "var(--mnf-f)" }} />
          </div>
        </GlassCard>
      </div>

      {/* Days in garden + weeks rooted */}
      <div style={{ padding: "12px 16px 0" }}>
        <GlassCard padding={14} organic data-testid="card-tenure">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className="inline-flex items-center justify-center"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "rgba(168,212,184,0.30)",
                  color: "var(--mnf-fd)",
                }}
              >
                <Icon name="park" size={20} />
              </div>
              <div>
                <div className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tx)", fontWeight: 600 }}>
                  {stats.daysInGarden} {stats.daysInGarden === 1 ? "day" : "days"} in the garden
                </div>
                <div className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}>
                  {stats.weeksRooted} {stats.weeksRooted === 1 ? "week" : "weeks"} rooted
                </div>
              </div>
            </div>
            <PillTag tone="fiber">{tierLabel}</PillTag>
          </div>
        </GlassCard>
      </div>

      {/* Spiritual discipline badges */}
      <div style={{ padding: "12px 16px 0" }}>
        {disciplineStates.length > 0 ? (
          <DisciplineBadgesCard userId={me.id} variant="today" />
        ) : (
          // DisciplineBadgesCard silently renders nothing when there is no
          // data yet, which used to read as "this section is broken" for a
          // brand-new sister rather than "you haven't started." Say so.
          <GlassCard padding={18} data-testid="card-disciplines-empty">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
                  Spiritual disciplines
                </p>
                <p
                  className="mnf-serif mt-1"
                  style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3 }}
                >
                  No disciplines tracked yet
                </p>
                <p className="mnf-body mt-1" style={{ fontSize: 12.5, color: "var(--mnf-tg)" }}>
                  Log a meal, a verse, a prayer, or a sabbath rest and your first badge starts here.
                </p>
              </div>
              <PillTag tone="neutral">Beginning</PillTag>
            </div>
          </GlassCard>
        )}
      </div>

      {/* My Account tile grid */}
      <div style={{ padding: "20px 16px 0" }}>
        <div
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-tg)", marginBottom: 10, paddingLeft: 4 }}
        >
          My account
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <AccountTile
            href="/profile/edit"
            icon="person"
            label="My profile"
            testid="tile-my-profile"
          />
          <AccountTile
            href="/billing"
            icon="credit_card"
            label="Billing"
            sublabel={last4 ? `•••• ${last4}` : "No card on file"}
            testid="tile-billing"
          />
          <AccountTile
            href="/billing"
            icon="receipt_long"
            label="Purchases"
            testid="tile-purchases"
          />
          <AccountTile
            href="/nourish"
            icon="restaurant"
            label="Favorite meals"
            testid="tile-favorite-meals"
          />
          <AccountTile
            href="/nourish"
            icon="menu_book"
            label="My recipes"
            testid="tile-my-recipes"
          />
          <AccountTile
            href="/move"
            icon="bookmark"
            label="Saved workouts"
            testid="tile-saved-workouts"
          />
          <AccountTile
            href="/sister-network"
            icon="diversity_3"
            label="Sister Network"
            testid="tile-sister-network"
          />
        </div>
      </div>

      {/* Location + Instagram */}
      <div style={{ padding: "20px 16px 0" }}>
        <div
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-tg)", marginBottom: 10, paddingLeft: 4 }}
        >
          About me
        </div>
        <GlassCard padding={0} data-testid="card-about">
          <div
            className="flex items-center gap-3"
            style={{ padding: "14px 16px", borderBottom: "1px solid rgba(30,74,53,0.08)" }}
          >
            <Icon name="location_on" size={20} style={{ color: "var(--mnf-f)" }} />
            <div className="flex-1">
              <div className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Location
              </div>
              <div className="mnf-body" style={{ fontSize: 14.5, color: "var(--mnf-tx)", fontWeight: 500 }} data-testid="text-location">
                {me.city || "Not set"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" style={{ padding: "14px 16px" }}>
            <Icon name="alternate_email" size={20} style={{ color: "var(--mnf-purple)" }} />
            <div className="flex-1">
              <div className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Instagram
              </div>
              <div className="mnf-body" style={{ fontSize: 14.5, color: "var(--mnf-tx)", fontWeight: 500 }} data-testid="text-instagram">
                {me.instagram ? `@${me.instagram}` : "Not set"}
              </div>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Settings */}
      <div style={{ padding: "20px 16px 0" }}>
        <div
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-tg)", marginBottom: 10, paddingLeft: 4 }}
        >
          Settings
        </div>
        <GlassCard padding={0} data-testid="card-settings-list">
          <ListRow
            href="/settings"
            icon="notifications"
            label="Notifications"
            testid="row-settings-notifications"
          />
          <ListRow
            href="/settings"
            icon="straighten"
            label="Units and preferences"
            testid="row-settings-units"
          />
          <ListRow
            href="/cycle"
            icon="favorite"
            label="Cycle and rhythm"
            testid="row-settings-cycle"
          />
        </GlassCard>
      </div>

      {/* Support */}
      <div style={{ padding: "20px 16px 0" }}>
        <div
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-tg)", marginBottom: 10, paddingLeft: 4 }}
        >
          Support
        </div>
        <GlassCard padding={0} data-testid="card-support-list">
          <ListRow
            href="/welcome-journey"
            icon="play_circle"
            label="Watch getting started"
            testid="row-support-getting-started"
          />
          <ListRow
            href="/share-journey"
            icon="lightbulb"
            label="Share your journey"
            testid="row-support-share-journey"
          />
          <ListRow
            icon="mail"
            label="Email support"
            testid="row-support-email"
            onClick={() => {
              window.location.href = "mailto:movenourishflourishsupport@gmail.com?subject=MNF%20support";
            }}
            trailing={
              <span className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}>
                support@
              </span>
            }
          />
          <ListRow
            href="/legal/terms"
            icon="gavel"
            label="Terms of Service"
            testid="row-support-terms"
          />
          <ListRow
            href="/legal/privacy"
            icon="shield"
            label="Privacy Policy"
            testid="row-support-privacy"
          />
          <ListRow
            href="/legal"
            icon="article"
            label="All legal documents"
            testid="row-support-legal"
          />
        </GlassCard>
      </div>

      {/* Sign out */}
      <div style={{ padding: "24px 16px 16px" }}>
        <Btn
          variant="secondary"
          block
          disabled={signingOut}
          onClick={async () => {
            // This used to be `setSignedOut(true)` — React state and nothing
            // else. It painted "You are signed out." while the Convex session,
            // the auth token and snapshot auto-save all stayed live; a refresh
            // dropped straight back into the account. On a shared device that
            // is not a cosmetic bug, it is the next person reading her data.
            if (!window.confirm("Sign out of Move Nourish Flourish?")) return;
            setSignOutError(null);
            setSigningOut(true);
            try {
              // Stop the snapshot writer BEFORE dropping the session, so a
              // debounced save cannot fire against a torn-down identity.
              stopAutoSave();
              await signOut();
              setCurrentUserOverride(null);
            } catch (err) {
              console.error("[profile] sign out failed", err);
              // Say so rather than painting the signed-out screen over a live
              // session — the exact failure this replaces.
              setSignOutError(
                "We couldn't sign you out. Check your connection and try again.",
              );
            } finally {
              setSigningOut(false);
            }
          }}
          data-testid="button-sign-out"
        >
          {signingOut ? "Signing out…" : "Sign out"}
        </Btn>
        {signOutError && (
          <p
            role="alert"
            className="mnf-body mt-2 text-center"
            style={{ fontSize: 12, color: "var(--mnf-clay, #C0784A)" }}
            data-testid="text-signout-error"
          >
            {signOutError}
          </p>
        )}
      </div>

      {/* Version footer */}
      <div style={{ textAlign: "center", padding: "0 16px 16px" }}>
        <div
          className="mnf-body italic"
          style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}
        >
          Move Nourish Flourish · v1.0.0
        </div>
      </div>
    </div>
  );
}
